/**
 * 메일 본문의 바깥 그림을 우리 서버 프록시로 돌린다 (2026-10-14 · 사용자 결정 「모든 외부 그림 프록시」).
 *
 * 보낸 쪽 서버가 `Cross-Origin-Resource-Policy: same-origin` 을 걸어 둔 그림(claude.ai 등)은 브라우저가
 * 우리 화면에 끼워 넣지 못하게 막는다. 서버(`GET /mail/messages/:id/image-proxy?u=`)가 대신 받아 준다.
 * ★ 주소 정규화는 서버(`normalizeImageUrl`)와 같아야 한다 — 서버는 «본문에 있는 주소»만 받아 주므로
 *   두 쪽이 다르게 적으면 404 다(`//host` → https · `new URL().toString()`).
 * ★ `srcset` 은 뗀다 — 남겨 두면 브라우저가 그쪽 주소로 바로 받아 다시 막힌다.
 */

export function normalizeImageUrl(raw: string): string | null {
  const v = raw.trim();
  const full = v.startsWith('//') ? `https:${v}` : v;
  try {
    const u = new URL(full);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.toString();
  } catch {
    return null;
  }
}

export function mailImageProxySrc(mailId: string, url: string): string {
  return `/api/v1/mail/messages/${encodeURIComponent(mailId)}/image-proxy?u=${encodeURIComponent(url)}`;
}

const CSS_URL = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)\s]*))\s*\)/gi;

/**
 * 거른 본문 HTML 의 바깥 그림 주소를 프록시 주소로 바꾼다. 우리 주소(`/api/…`)·cid·data 는 그대로.
 * ① `<img src>` ② 2026-10-14 추가 — 인라인 `style` 의 `url(…)`(배경 그림). 서버도 같은 둘을 «본문에 있는 주소»로 센다.
 */
export function proxyExternalImages(safeHtml: string, mailId: string | null | undefined): string {
  if (!mailId || !safeHtml || !/<img|url\(/i.test(safeHtml)) return safeHtml;
  const doc = new DOMParser().parseFromString(`<body>${safeHtml}</body>`, 'text/html');
  let changed = false;
  for (const img of Array.from(doc.body.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    const n = /^(https?:)?\/\//i.test(src.trim()) ? normalizeImageUrl(src) : null;
    if (!n) continue;
    img.setAttribute('src', mailImageProxySrc(mailId, n));
    img.removeAttribute('srcset');
    changed = true;
  }
  for (const el of Array.from(doc.body.querySelectorAll<HTMLElement>('[style]'))) {
    const style = el.getAttribute('style') ?? '';
    if (!/url\(/i.test(style)) continue;
    const next = style.replace(CSS_URL, (whole, a?: string, b?: string, c?: string) => {
      const raw = a ?? b ?? c ?? '';
      const n = /^(https?:)?\/\//i.test(raw.trim()) ? normalizeImageUrl(raw) : null;
      return n ? `url("${mailImageProxySrc(mailId, n)}")` : whole;
    });
    if (next !== style) { el.setAttribute('style', next); changed = true; }
  }
  return changed ? doc.body.innerHTML : safeHtml;
}
